import { CustomImage, EmailWithAttachment, ProductType } from '../models';

export interface Diagram {
  nodes: DiagramNode[];
  links: DiagramLink[];
  globalErrors?: DiagramError[];
}

export interface DiagramContextualMenu {
  x: number;
  y: number;
  node?: DiagramNode;
  isDeletable?: boolean;
  link?: DiagramLinkImpl;
}

export enum DiagramContextualMenuEnum {
  CREATE_LINK = 'create-link',
  ADD_MODULE = 'add-module',
  ADD_SECTION = 'add-section',
  ADD_GATEWAY_IF = 'add-gateway-if',
  ADD_GATEWAY_OR = 'add-gateway-or',
  ADD_GATEWAY_AND = 'add-gateway-and',
  ADD_EVENT_EMAIL = 'add-event-email',
  ADD_EVENT_END = 'add-event-end',
  DELETE_NODE = 'delete-node',
  DELETE_LINK = 'delete-link',
}

export class DiagramStateModel implements Diagram {
  linksImpl: DiagramLinkImpl[];
  nodes: DiagramNode[];
  links: DiagramLink[];
  nodeActive: boolean;
  valid: boolean;
  contextualMenu: DiagramContextualMenu;
  globalErrors: DiagramError[];
  lastError?: DiagramError;
  translation: {
    x: number;
    y: number;
  };

  constructor(diagramStateModel?: DiagramStateModel) {
    this.linksImpl = (diagramStateModel?.linksImpl || []).map(l => l);
    this.nodes = (diagramStateModel?.nodes || []).map(n => DiagramNode.clone(n));
    this.links = (diagramStateModel?.links || []).map(l => l);
    this.valid = diagramStateModel.valid ? diagramStateModel.valid : false;
    this.globalErrors = [];
  }
}

export interface DiagramError {
  code: string;
  source?: DiagramNode;
  extra?: any;
}

/** Represent a shapes in the diagram: Pedagogical Module, Event, Gateway */
export abstract class DiagramNode {
  slug?: string;
  x: number;
  y: number;
  type: DiagramNodeType;
  errors?: DiagramError[];

  protected constructor(node?: DiagramNode) {
    this.slug = node?.slug;
    this.x = node?.x;
    this.y = node?.y;
    this.type = node?.type;
    this.errors = [];
  }

  static clone(node: DiagramNode): DiagramNode {
    switch (node.type) {
      case DiagramNodeType.TASK:
        return new DiagramTask(<DiagramTask>node);
      case DiagramNodeType.EVENT:
        return new DiagramEvent(<DiagramEvent>node);
      case DiagramNodeType.GATEWAY:
        return new DiagramGateway(<DiagramGateway>node);
    }
  }
}

export enum DiagramNodeType {
  TASK = 'task',
  EVENT = 'event',
  GATEWAY = 'gateway',
}

export class DiagramTask extends DiagramNode {
  taskType?: ProductType;
  name: string;
  productSlug?: string;
  sectionDetails?: {
    productsSlugs?: { slug: string; order?: number }[];
    chained?: boolean;
    description: string;
    picture: CustomImage;
    picturesCustomList: CustomImage[];
  };

  constructor(task?: DiagramTask) {
    super(task);
    this.taskType = task?.taskType;
    this.name = task?.name || '';
    this.productSlug = task?.productSlug;
    this.sectionDetails = {
      productsSlugs: task?.sectionDetails?.productsSlugs.map(p => p),
      chained: !!task?.sectionDetails?.chained,
      description: task?.sectionDetails?.description,
      picture: task?.sectionDetails?.picture ?? new CustomImage(task.sectionDetails?.picture),
      picturesCustomList: task?.sectionDetails?.picturesCustomList.map(p => new CustomImage(p)) || [],
    };
  }
}

export class DiagramEvent extends DiagramNode {
  eventType: DiagramEventType;
  template?: EmailWithAttachment;

  constructor(event?: DiagramEvent) {
    super(event);
    this.eventType = event?.eventType;
    if (event?.eventType === DiagramEventType.EMAIL) {
      this.template = new EmailWithAttachment(event?.template);
    }
  }
}

export enum DiagramEventType {
  START = 'start',
  EMAIL = 'email',
  END = 'end',
}

export class DiagramGatewayCondition {
  nodeSlug?: string;
  linkSlug: string;
  minScore: number;
  maxScore: number;
}

export class DiagramGateway extends DiagramNode {
  gatewayType: DiagramGatewayType;
  conditions?: DiagramGatewayCondition[];
  section?: boolean;
  linkedNode?: DiagramNode;

  constructor(gateway?: DiagramGateway) {
    super(gateway);
    this.gatewayType = gateway?.gatewayType;
  }
}

export enum DiagramGatewayType {
  OR = 'or',
  AND = 'and',
  END_AND = 'end-and',
  IF = 'if',
}

export interface DiagramLink {
  slug: string;
  source: string; // slug of corresponding node
  target: string; // slug of corresponding node
}

export interface DiagramLinkImpl {
  slug: string;
  source: DiagramNode;
  target: DiagramNode;
}

export const defaultStart: DiagramEvent = {
  x: 50,
  y: 100,
  type: DiagramNodeType.EVENT,
  eventType: DiagramEventType.START,
};

export const defaultFirstTask: DiagramTask = {
  x: 250,
  y: 100,
  type: DiagramNodeType.TASK,
  name: '',
};
